
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/context/LanguageContext";

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        console.error("Verification failed: No token provided");
        setError("無效的驗證連結");
        setIsVerifying(false);
        return;
      }

      try {
        console.log("Starting verification with token:", token);
        
        // 1. 檢查令牌是否有效
        const { data: tokenData, error: tokenError } = await supabase
          .from("verification_tokens")
          .select("*")
          .eq("token", token)
          .single();

        if (tokenError || !tokenData) {
          console.error("Token verification error:", tokenError || "No token data found");
          setError("驗證連結已過期或無效");
          setIsVerifying(false);
          return;
        }

        const userId = tokenData.user_id;
        console.log("Token valid for user ID:", userId);

        // 2. 檢查令牌是否已過期
        const now = new Date();
        const expiresAt = new Date(tokenData.expires_at);
        if (now > expiresAt) {
          console.error("Token expired, expiration:", expiresAt);
          setError("驗證連結已過期");
          setIsVerifying(false);
          return;
        }

        // 3. 更新使用者的驗證狀態
        const { error: updateError } = await supabase
          .from("profiles")
          .update({ is_verified: true })
          .eq("id", userId);

        if (updateError) {
          console.error("Profile update error:", updateError);
          throw updateError;
        }

        console.log("User verified successfully");

        // 4. 刪除已使用的令牌
        await supabase
          .from("verification_tokens")
          .delete()
          .eq("token", token);

        // 5. 顯示成功訊息並重定向
        toast({
          title: "電子郵件驗證成功！",
          description: "您的帳號已經完成驗證",
        });

        // 檢查用戶是否已登入
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData.session) {
          // 已登入，導向首頁
          navigate("/");
        } else {
          // 未登入，導向登入頁
          navigate("/login");
        }
      } catch (err: any) {
        console.error("Verification error:", err);
        setError(err.message || "驗證過程中發生錯誤");
        setIsVerifying(false);
      }
    };

    verifyEmail();
  }, [token, navigate, toast]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-md space-y-8 rounded-xl bg-white p-8 shadow-lg text-center">
        {isVerifying ? (
          <>
            <h1 className="text-2xl font-bold mb-4">正在驗證您的電子郵件...</h1>
            <div className="flex justify-center">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          </>
        ) : error ? (
          <>
            <h1 className="text-2xl font-bold text-red-600 mb-4">驗證失敗</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="flex flex-col space-y-4">
              <button
                onClick={() => navigate("/signup")}
                className="bg-primary py-2 px-4 rounded text-white"
              >
                重新註冊
              </button>
              <button
                onClick={() => navigate("/")}
                className="bg-gray-200 py-2 px-4 rounded"
              >
                返回首頁
              </button>
            </div>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-green-600 mb-4">驗證成功！</h1>
            <p className="text-gray-600 mb-6">您的電子郵件已成功驗證</p>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
